import React, { useEffect, useState } from "react";

import EmailBlacklistService from "../services/emailblacklist.service";
import Spinner from "./Spinner";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const errMsg = (err) =>
  (err.response && err.response.data && err.response.data.message) || err.message;

// Admin panel: manage email patterns that are not allowed to sign up.
const EmailBlacklist = () => {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ pattern: "", type: "wildcard", note: "" });
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = () => {
    setLoading(true);
    EmailBlacklistService.getAll()
      .then((res) => setRules(res.data || []))
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const add = (e) => {
    e.preventDefault();
    if (!form.pattern.trim()) return;
    setSaving(true);
    EmailBlacklistService.create(form)
      .then(() => {
        toast.success(t("blacklist.added"));
        setForm({ ...form, pattern: "", note: "" });
        setTestResult(null);
        load();
      })
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setSaving(false));
  };

  const remove = (r) => {
    if (!window.confirm(t("blacklist.confirmDelete", { pattern: r.pattern }))) return;
    EmailBlacklistService.remove(r.id)
      .then(() => {
        toast.success(t("blacklist.deleted"));
        setTestResult(null);
        load();
      })
      .catch((err) => toast.error(errMsg(err)));
  };

  const runTest = (e) => {
    e.preventDefault();
    if (!testEmail.trim()) return;
    EmailBlacklistService.test(testEmail.trim())
      .then((res) => setTestResult(res.data))
      .catch((err) => toast.error(errMsg(err)));
  };

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-body">
        <h5 className="card-title">{t("blacklist.title")}</h5>
        <p className="text-muted small">{t("blacklist.help")}</p>

        <form className="row g-2 mb-3" onSubmit={add}>
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder={
                form.type === "regex"
                  ? t("blacklist.regexPlaceholder")
                  : t("blacklist.wildcardPlaceholder")
              }
              value={form.pattern}
              maxLength={255}
              onChange={(e) => setForm({ ...form, pattern: e.target.value })}
            />
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              <option value="wildcard">{t("blacklist.typeWildcard")}</option>
              <option value="regex">{t("blacklist.typeRegex")}</option>
            </select>
          </div>
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder={t("blacklist.notePlaceholder")}
              value={form.note}
              maxLength={255}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </div>
          <div className="col-md-2 d-grid">
            <button className="btn btn-danger" type="submit" disabled={saving || !form.pattern.trim()}>
              {t("blacklist.add")}
            </button>
          </div>
        </form>

        {loading ? (
          <Spinner small />
        ) : rules.length === 0 ? (
          <p className="text-muted mb-3">{t("blacklist.empty")}</p>
        ) : (
          <div className="table-responsive rounded mb-3">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>{t("blacklist.pattern")}</th>
                  <th style={{ width: 120 }}>{t("blacklist.type")}</th>
                  <th>{t("blacklist.note")}</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id}>
                    <td><code>{r.pattern}</code></td>
                    <td>
                      <span className={`badge ${r.type === "regex" ? "bg-dark" : "bg-secondary"}`}>
                        {r.type === "regex" ? t("blacklist.typeRegex") : t("blacklist.typeWildcard")}
                      </span>
                    </td>
                    <td className="small text-muted">{r.note}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(r)}>
                        {t("common.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form className="row g-2 align-items-center" onSubmit={runTest}>
          <div className="col-md-6">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t("blacklist.testPlaceholder")}
              value={testEmail}
              onChange={(e) => {
                setTestEmail(e.target.value);
                setTestResult(null);
              }}
            />
          </div>
          <div className="col-auto">
            <button className="btn btn-sm btn-outline-secondary" type="submit">
              {t("blacklist.test")}
            </button>
          </div>
          {testResult && (
            <div className="col-auto small">
              {testResult.blocked ? (
                <span className="text-danger">
                  {t("blacklist.testBlocked", { pattern: testResult.rule.pattern })}
                </span>
              ) : (
                <span className="text-success">{t("blacklist.testAllowed")}</span>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EmailBlacklist;
